import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dataHealthApi } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Activity, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function DataHealthPage() {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('budget'); // 'budget' | 'actual'
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [brandFilter, setBrandFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    dataHealthApi.get({ year })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year]);

  const filteredStores = useMemo(() => {
    if (!data) return [];
    return data.stores.filter(s => {
      if (brandFilter !== 'all' && s.brand_name !== brandFilter) return false;
      if (onlyGaps) {
        const covered = scenario === 'budget' ? s.budget_covered : s.actual_covered;
        return covered < 12;
      }
      return true;
    });
  }, [data, brandFilter, onlyGaps, scenario]);

  const brands = data?.by_brand?.map(b => b.brand) || [];

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6" data-testid="data-health-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="w-7 h-7 text-slate-700" /> Stato Dati
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Copertura Budget e Attuali per tutti i negozi × 12 mesi. Usa i buchi rossi per sapere dove importare.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 rounded-none" data-testid="data-health-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Link to="/imports">
            <Button className="rounded-none h-9 bg-black text-white hover:bg-slate-900" data-testid="data-health-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Vai a Import
            </Button>
          </Link>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-400">Caricamento...</p>}

      {data && (
        <>
          {/* Riepilogo Hero dark */}
          <div className="bg-slate-900 text-white p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <HeroTile
                label="Negozi attivi"
                value={data.total_stores}
                sub={`${data.total_cells} celle per scenario`}
              />
              <HeroTile
                label="Copertura Budget"
                value={`${data.budget_coverage_pct}%`}
                sub={`${data.total_budget_cells_covered} / ${data.total_cells} mesi`}
                tone={data.budget_coverage_pct >= 90 ? 'positive' : data.budget_coverage_pct >= 50 ? 'neutral' : 'negative'}
              />
              <HeroTile
                label="Copertura Attuali"
                value={`${data.actual_coverage_pct}%`}
                sub={`${data.total_actual_cells_covered} / ${data.total_cells} mesi`}
                tone={data.actual_coverage_pct >= 90 ? 'positive' : data.actual_coverage_pct >= 50 ? 'neutral' : 'negative'}
              />
              <HeroTile
                label="Anno"
                value={data.year}
                sub={data.by_brand.length + ' brand'}
              />
            </div>
          </div>

          {/* Tabella riepilogo per brand */}
          <div className="bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200">
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">Copertura per brand</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Brand</th>
                  <th className="text-right px-4 py-2">Negozi</th>
                  <th className="text-right px-4 py-2">Budget</th>
                  <th className="text-right px-4 py-2">Attuali</th>
                </tr>
              </thead>
              <tbody>
                {data.by_brand.map(b => (
                  <tr key={b.brand} className="border-t border-slate-100" data-testid={`brand-row-${b.brand}`}>
                    <td className="px-4 py-2 font-medium">{b.brand}</td>
                    <td className="px-4 py-2 text-right">{b.stores}</td>
                    <td className="px-4 py-2 text-right">
                      <CoverageChip pct={b.budget_coverage_pct} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <CoverageChip pct={b.actual_coverage_pct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Matrice per negozio */}
          <div className="bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">Matrice per negozio</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex border border-slate-200 text-xs" data-testid="data-health-scenario-tabs">
                  {[{ k: 'budget', l: 'Budget' }, { k: 'actual', l: 'Attuali' }].map(o => (
                    <button
                      key={o.k}
                      onClick={() => setScenario(o.k)}
                      className={`px-3 py-1.5 ${scenario === o.k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`scenario-tab-${o.k}`}
                    >{o.l}</button>
                  ))}
                </div>
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="w-44 rounded-none h-8 text-xs" data-testid="data-health-brand-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i brand</SelectItem>
                    {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="data-health-only-gaps">
                  <input
                    type="checkbox"
                    checked={onlyGaps}
                    onChange={(e) => setOnlyGaps(e.target.checked)}
                    className="rounded-none border-slate-300"
                  />
                  Solo negozi con buchi
                </label>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10">Negozio</th>
                    <th className="text-left px-3 py-2">Brand</th>
                    {MONTHS.map(m => <th key={m} className="px-1 py-2 text-center w-9">{m}</th>)}
                    <th className="px-3 py-2 text-right">Cop.</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStores.map(s => {
                    const cells = scenario === 'budget' ? s.budget_months : s.actual_months;
                    const covered = scenario === 'budget' ? s.budget_covered : s.actual_covered;
                    return (
                      <tr key={s.store_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`store-row-${s.store_id}`}>
                        <td className="px-3 py-1.5 font-medium sticky left-0 bg-white hover:bg-slate-50 z-10">
                          <Link to={`/stores/${s.store_id}`} className="text-slate-900 hover:underline">{s.display_name}</Link>
                        </td>
                        <td className="px-3 py-1.5 text-slate-500">{s.brand_name}</td>
                        {cells.map((c, i) => (
                          <td key={MONTHS[i]} className="px-0.5 py-1.5 text-center">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`inline-block w-6 h-5 ${c.has_value ? 'bg-emerald-400' : 'bg-red-300'}`}
                                  data-testid={`cell-${scenario}-${s.store_id}-${i + 1}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <span className="font-medium">{MONTHS[i]}</span>: {c.has_value ? `Presente (${Math.round(c.value).toLocaleString('it-IT')} €)` : 'Mancante'}
                                {c.source && <span className="text-[10px] block opacity-70">source: {c.source}</span>}
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        ))}
                        <td className="px-3 py-1.5 text-right font-mono">
                          <span className={covered === 12 ? 'text-emerald-600' : covered >= 6 ? 'text-amber-600' : 'text-red-600'}>
                            {covered}/12
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredStores.length === 0 && (
                    <tr>
                      <td colSpan={15} className="text-center text-slate-400 italic py-8">
                        Nessun negozio corrisponde ai filtri selezionati.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-4 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 bg-emerald-400" /> Valore presente
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 bg-red-300" /> Valore mancante
              </span>
              <span className="ml-auto text-slate-400">Hover per vedere il valore esatto.</span>
            </div>
          </div>
        </>
      )}
    </div>
    </TooltipProvider>
  );
}

function HeroTile({ label, value, sub, tone }) {
  const toneColor = {
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    neutral: 'text-amber-400',
  }[tone] || 'text-white';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
      <p className={`font-heading text-2xl md:text-4xl font-bold mt-1 ${toneColor}`}>{value}</p>
      <p className="text-xs text-slate-400 mt-1">{sub}</p>
    </div>
  );
}

function CoverageChip({ pct }) {
  const isFull = pct >= 99.9;
  const isLow = pct < 50;
  const Icon = isFull ? CheckCircle2 : AlertTriangle;
  const cls = isFull ? 'text-emerald-600' : isLow ? 'text-red-600' : 'text-amber-600';
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-sm ${cls}`}>
      <Icon className="w-3.5 h-3.5" /> {pct.toFixed(1)}%
    </span>
  );
}
